import { fastapi } from './api';
import { demoStore, isDemoModeActive } from './demoModeService';

export interface ClinicSchedule {
  id?: number;
  resource_id: number;
  day_of_week: number;
  start_time: string;
  end_time: string;
  default_appointment_duration?: number;
  created_at?: string;
  updated_at?: string;
}

export interface ClinicHoliday {
  id: number;
  fecha: string;
  esta_abierto: boolean;
  hora_apertura_especial: string | null;
  hora_cierre_especial: string | null;
  descripcion: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface ClinicScheduleLookup {
  date: string;
  is_open: boolean;
  is_holiday: boolean;
  holiday_description: string | null;
  schedules: ClinicSchedule[];
}

export const clinicSchedulesService = {
  // Schedules
  getSchedules: async (): Promise<ClinicSchedule[]> => {
    if (isDemoModeActive()) return demoStore.mutate(() => {}).schedules;
    const res = await fastapi.get('/clinics/schedules/');
    return res.data;
  },
  
  createSchedule: async (data: Partial<ClinicSchedule>): Promise<ClinicSchedule> => {
    if (isDemoModeActive()) { let created!: ClinicSchedule; demoStore.mutate(state => { created = { id: demoStore.nextId(state), resource_id: 8201, day_of_week: 1, start_time: '09:00', end_time: '18:00', ...data }; state.schedules.push(created); }); return created; }
    const res = await fastapi.post('/clinics/schedules/', data);
    return res.data;
  },
  
  updateSchedule: async (id: number, data: Partial<ClinicSchedule>): Promise<ClinicSchedule> => {
    if (isDemoModeActive()) { let updated!: ClinicSchedule; demoStore.mutate(state => { updated = state.schedules.find(item => item.id === id); if (updated) Object.assign(updated, data); }); return updated; }
    const res = await fastapi.put(`/clinics/schedules/${id}`, data);
    return res.data;
  },
  
  deleteSchedule: async (id: number): Promise<void> => {
    if (isDemoModeActive()) { demoStore.mutate(state => { state.schedules = state.schedules.filter(item => item.id !== id); }); return; }
    await fastapi.delete(`/clinics/schedules/${id}`);
  },
  
  // Holidays
  getHolidays: async (): Promise<ClinicHoliday[]> => {
    if (isDemoModeActive()) return demoStore.mutate(() => {}).holidays;
    const res = await fastapi.get('/clinics/holidays/');
    return res.data;
  },
  
  createHoliday: async (data: Partial<ClinicHoliday>): Promise<ClinicHoliday> => {
    if (isDemoModeActive()) { let created!: ClinicHoliday; demoStore.mutate(state => { created = { id: demoStore.nextId(state), fecha: new Date().toISOString().slice(0, 10), esta_abierto: false, hora_apertura_especial: null, hora_cierre_especial: null, descripcion: null, ...data }; state.holidays.push(created); }); return created; }
    const res = await fastapi.post('/clinics/holidays/', data);
    return res.data;
  },
  
  updateHoliday: async (id: number, data: Partial<ClinicHoliday>): Promise<ClinicHoliday> => {
    if (isDemoModeActive()) { let updated!: ClinicHoliday; demoStore.mutate(state => { updated = state.holidays.find(item => item.id === id); if (updated) Object.assign(updated, data); }); return updated; }
    const res = await fastapi.put(`/clinics/holidays/${id}`, data);
    return res.data;
  },
  
  deleteHoliday: async (id: number): Promise<void> => {
    if (isDemoModeActive()) { demoStore.mutate(state => { state.holidays = state.holidays.filter(item => item.id !== id); }); return; }
    await fastapi.delete(`/clinics/holidays/${id}`);
  },
  
  // Lookup
  lookupSchedule: async (date: string): Promise<ClinicScheduleLookup> => {
    if (isDemoModeActive()) return { date, is_open: true, is_holiday: false, holiday_description: null, schedules: demoStore.mutate(() => {}).schedules };
    const res = await fastapi.get(`/clinics/schedules/lookup/${date}`);
    return res.data;
  },
  
  // Seed defaults
  seedDefaults: async (): Promise<{status: string}> => {
    if (isDemoModeActive()) return { status: 'success' };
    const res = await fastapi.post('/clinics/schedules/seed-defaults');
    return res.data;
  }
};
